/* System defined core library */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Observable, Observer, Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { LoaderService } from '../../../shared/services/loader.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { TimerService } from '../../../shared/services/timer.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { MessagingApiService } from '../../shared/services/messaging-api.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { PaymentApiService } from '../../shared/services/payment-api.service';
import { RegistrationApiService } from '../../shared/services/registration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { EmailResponse } from '../../../shared/models/common/shared.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { CardDetail } from '../../../shared/models/scheduling/carddetail.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { EligibilityDetail } from '../../../shared/models/scheduling/eligibility.model';
import { MetricDetails, MetricRequest } from '../../../shared/models/scheduling/metrics.model';
import { PaymentOption } from '../../../shared/models/scheduling/paymentOption.model';
import { CreatePaymentSessionRequest, CreatePaymentSessionResponse } from '../../../shared/models/scheduling/paymentSession.model';
import { PersonDetail } from '../../../shared/models/scheduling/personalDetail.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { ReminderDetails } from '../../../shared/models/scheduling/reminderdetail.model';
import { Session } from '../../../shared/models/scheduling/session.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';
import { Parameter, TestingAccommodation } from '../../../shared/models/scheduling/testingAccommodation.model';

/* User defined enums/constants */
import { SchedulingErrors, voucherPaymentCodes } from '../../../shared/constants/scheduling.constant';
import { CardFee, ComponentType, LogLevel, NavigationAction } from '../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-schedule-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss']
})
export class SchedulePaymentComponent implements OnInit {

  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    this.resetVoucher();
    
    this.metricApiService.logNavigation('Payment', NavigationAction.backwardsNavigation);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: any) {
    if (this.isBookingInProgress) {
      let holdId = '';
      if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        holdId = this.selectedAppointments[0].exam.sessions[0].hold.id;
      } else {
        holdId = this.selectedAppointments[0].hold.id;
      }

      this.metricApiService.log(LogLevel.Debug, `Candidate unloaded the Payment page while booking was in progress.\nHold: ${holdId}`);
    }
  }

  /* User defined private/public variables */
  public acceptsVoucherPayment: boolean = false;
  public balance: number = 0.00;
  public booking: Booking;
  public candidate: Candidate;
  public candidateDetail: any;
  public candidateGuid: string = '';
  public cardDetail = new CardDetail();
  public client: Client;
  public confirmationNumber: string = "";
  public countries: Country[];
  public currencyCode: string;
  public discountAmount: number = 0.00;
  public eligibility: EligibilityDetail[] = [];
  public emailResponse: EmailResponse;
  public errorMessage: any;
  public isBackToBackRequired: boolean = false;
  public isBookingInProgress: boolean = false;
  public isVoucherFormValid: boolean = false;
  public isVoucherFullPayment: boolean = false;
  public isVoucherValid: boolean = false;
  public metricsDetail: MetricDetails[];
  public paymentOptions: PaymentOption[] = new Array();
  public paymentOrchestratorSessionDetail: CreatePaymentSessionResponse;
  public personDetail: PersonDetail;
  public program: Program;
  public reminderDetails = new ReminderDetails();
  public scheduledConfirmationNums: string[] = [];
  public selectedAppointments: AppointmentDetail[] = [];
  public selectedSite: SiteSlot;
  public showPostingInProgress: boolean = false;
  public skipConfirmPage: boolean = false;
  public terminationUrl: string = '';
  public totalAmount: number = 0.00;
  public voucherAttempts: number;
  public voucherDetail = new CardDetail();
  public voucherErrorMessage: boolean = false;

  /* Service subscriptions */
  candidateData$: Subscription;
  candidateGuid$: Subscription;
  client$: Subscription;
  countries$: Subscription;
  discountAmount$: Subscription;
  eligibility$: Subscription;
  metricsDetail$: Subscription;
  paymentOptions$: Subscription;
  paymentOrchestratorSessionDetail$: Subscription;
  program$: Subscription;
  selectedAppointments$: Subscription;
  selectedSite$: Subscription;
  translateLanguage$: Subscription;
  voucherAttempts$: Subscription;
  voucherDetail$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private messagingApiService: MessagingApiService,
    private metricApiService: MetricApiService,
    private paymentApiService: PaymentApiService,
    private registrationApiService: RegistrationApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private translate: TranslateService,
    private titleService: Title,
    private utilityService: UtilityService,
    private loaderService: LoaderService
  ) {
    this.candidateData$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });

    this.candidateGuid$ = selectedExamDetailService.candidateGuid.subscribe(
      guid => {
        this.candidateGuid = guid;
      });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.countries$ = selectedExamDetailService.countries.subscribe(
      countries => {
        this.countries = countries;
      });

    this.discountAmount$ = selectedExamDetailService.discountAmount.subscribe(
      discountAmount => {
        this.discountAmount = discountAmount;
      });

    this.eligibility$ = selectedExamDetailService.eligibilityDetails.subscribe(
      eligibility => {
        this.eligibility = eligibility;
      });

    this.metricsDetail$ = selectedExamDetailService.metricsDetail.subscribe(
      metricsDetail => {
        this.metricsDetail = metricsDetail;
      });

    this.paymentOptions$ = selectedExamDetailService.paymentOptions.subscribe(
      paymentOptions => {
        this.paymentOptions = paymentOptions || [];

        // to check if voucher is a payment options
        this.acceptsVoucherPayment = this.paymentOptions.some(elm => voucherPaymentCodes.indexOf(elm.id.toLowerCase()) !== -1);
      });

    this.paymentOrchestratorSessionDetail$ = selectedExamDetailService.paymentOrchestratorSessionDetail.subscribe(
      paymentOrchestratorSessionDetail => {
        this.paymentOrchestratorSessionDetail = paymentOrchestratorSessionDetail;
      });

    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.selectedExamDetailService.textReminder.subscribe(
      reminder => {
        if (reminder && reminder.mobileNumber && reminder.countryISDCode) {
          this.reminderDetails.notificationSelected = reminder.isNotificationSelected;
          this.reminderDetails.contactNumber = reminder.countryISDCode.concat(reminder.mobileNumber);
        }
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.selectedSite$ = selectedExamDetailService.siteSlot.subscribe(
      siteSlot => {
        this.selectedSite = siteSlot;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.voucherAttempts$ = selectedExamDetailService.voucherAttempts.subscribe(
      voucherAttempts => {
        this.voucherAttempts = voucherAttempts;
      });

    this.voucherDetail$ = selectedExamDetailService.voucherDetail.subscribe(
      voucherDetail => {
        this.voucherDetail = voucherDetail;
      });

    this.isBackToBackRequired = this.selectedExamDetailService.isBackToBackRequired;
    this.skipConfirmPage = this.selectedExamDetailService.skipConfirmPage;
    this.terminationUrl = this.selectedExamDetailService.terminationUrl;

    if (this.selectedExamDetailService.scheduleFee &&
      this.selectedExamDetailService.scheduleFee['totalAmount'] !== 0 &&
      this.selectedExamDetailService.scheduleFee['currency'] !== null) {
      this.totalAmount = this.selectedExamDetailService.scheduleFee['totalAmount'];
      this.currencyCode = this.selectedExamDetailService.scheduleFee['currency'];
    }
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.PaymentComponent)) return;

    // Need to populate countries if candidate is scheduling via proctor with voucher
    if (this.selectedExamDetailService.isProctored && this.acceptsVoucherPayment && (!this.countries || !this.countries.length)) {
      this.schedulingApiService.getCountries().subscribe(countries => {
        this.selectedExamDetailService.setCountries(countries);
      });
    }

    if (!!this.voucherDetail.number) {
      this.isVoucherValid = true;
    }
    if (!!this.discountAmount) {
      this.balance = this.totalAmount - this.discountAmount;
    }
    this.isBookingInProgress = false;
  }

  /* Use to go back from current screen */
  goBack(): void {
    this.routingService.backwardNavigation(ComponentType.PaymentComponent);
  }

  /**
   * Check Voucher form Valid
   * @param isFormValid
   */
  checkVoucherFormValid(isFormValid: boolean): void {
    //form valid state to disable submit button if form not valid
    this.isVoucherFormValid = isFormValid;
    // set full payment to false once modified
    this.isVoucherFullPayment = false;

  }

  getVoucherDetail(): void {
    this.resetVoucher();
    this.voucherErrorMessage = false;
  }

  /**
   * validate voucher
   * @param voucherNumber
   */
  validateVoucher(voucherNumber: string) {
    const voucher = new CardDetail(
      null,
      voucherNumber,
      this.selectedExamDetailService.scheduleFee['currency']
    );
    let voucherRequestModel = {};
    let examDetails: any[] = [];

    this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
      examDetails.push({
        "examId": appointment.exam.id,
        "amount": appointment.payments[0].amount
      });
    });
    voucherRequestModel['card'] = voucher;
    voucherRequestModel['sessions'] = examDetails;
    // just picking first exam for now
    voucherRequestModel['examDuration'] = this.selectedAppointments[0].appointmentSlot.duration;
    voucherRequestModel['applicationCode'] = 'pro';
    voucherRequestModel['siteId'] = this.selectedSite ? this.selectedSite.id : this.selectedExamDetailService.proctorSiteId;
    voucherRequestModel['siteCountry'] = this.selectedSite ? this.selectedSite.location.address.country : this.countries.find(c => c.name.toLowerCase() === this.candidate.details['COUNTRY'].toLowerCase()).id;
    voucherRequestModel['testDate'] = moment(this.selectedAppointments[0].appointmentSlot.startDateTime).format('YYYY-MM-DDTHH:mm:ss');

    this.paymentApiService.validateVoucherDetails(voucherRequestModel, "").subscribe(response => {
      if (response.isValid) {
        voucher.type = response.paymentType; // Get voucher type from API
        this.selectedExamDetailService.voucherTransactionId = response.transactionId;
        const discount = Math.abs(response.discountAmount);
        this.selectedExamDetailService.setDiscountAmount(discount);
        this.balance = Math.abs(this.totalAmount - discount);
        // this is assuming that discount amount would be amount on voucher
        // check if voucher can be a complete payment
        if (discount >= this.selectedExamDetailService.scheduleFee['totalAmount']) {
          this.selectedExamDetailService.isVoucherFullPayment = true;
          this.isVoucherFullPayment = true;
        } else {
          this.selectedExamDetailService.isVoucherFullPayment = false;
          this.isVoucherFullPayment = false;
        }
        this.voucherErrorMessage = false;
        this.isVoucherValid = true;
        this.selectedExamDetailService.setVoucherDetail(voucher);
      } else {
        this.resetVoucher();
        this.voucherErrorMessage = true;
        this.selectedExamDetailService.incrementVoucherAttempts();
        if (this.voucherAttempts === this.selectedExamDetailService.voucherAttemptsforRedirect) {
          this.metricApiService.log(LogLevel.Debug,
            'Voucher attempt threshold met: User Redirected to slots page.');
          //Redirect to Slot selection
          this.routingService.scheduleRedirectToSlotSelection();
        } else if (this.voucherAttempts === this.selectedExamDetailService.voucherAttemptsforLock) {
          this.metricApiService.log(LogLevel.Debug, 'Voucher attempt threshold met: User disallowed from attempting additional voucher IDs.');
        }
      }
    })
  }

  /**
   * Submit
   * @param event
   */
  submit(event: any): void {
    this.isBookingInProgress = true;
    if (this.isVoucherFullPayment) {
      this.submitVoucher();
    } else {
      this.redirectToPayment();
    }
  }

  /* submit using full voucher payment flow */
  submitVoucher(): void {
    this.timerService.clearClock();

    let examDetails: any[] = [];
    let cardDetail: any = null;
    let voucherExamDetails: any[] = [];
    let voucherDetail: any = null;

    this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
      if (!this.isVoucherFullPayment) {
        examDetails.push({
          "examId": appointment.exam.id,
          "amount": this.isVoucherFullPayment ? 0 : appointment.payments[0].amount - this.discountAmount
        });
      }
      if (this.selectedExamDetailService.voucherTransactionId) {
        voucherExamDetails.push({
          "examId": appointment.exam.id,
          "amount": this.isVoucherFullPayment ? appointment.payments[0].amount : this.discountAmount
        });
      }
    });

    this.cardDetail.currency = this.currencyCode;
    this.cardDetail.billingAddressPostalCode = this.candidate.details['ZIPCODE'];
    if (!this.isVoucherFullPayment) {
      cardDetail = {
        "card": this.cardDetail,
        "sessions": examDetails,
        "candidateId": '',
        "siteCountry": this.selectedSite ? this.selectedSite.location.address.country : this.selectedExamDetailService.proctorCountry
      };
    }
    if (this.selectedExamDetailService.voucherTransactionId) {
      voucherDetail = {
        "card": this.voucherDetail,
        "sessions": voucherExamDetails,
        "candidateId": '',
        "voucherTransactionId": this.selectedExamDetailService.voucherTransactionId,
      };
    }

    let tests: any[] = [];
    this.selectedAppointments.forEach((selectedAppointment: AppointmentDetail) => {
      let postTestingAccommodations: any[] = [];
      if (selectedAppointment.exam.testingAccommodations != undefined && selectedAppointment.exam.testingAccommodations.length) {
        selectedAppointment.exam.testingAccommodations.forEach((testingAccom: TestingAccommodation) => {
          if (testingAccom.controlType != "LIST") {
            postTestingAccommodations.push({ id: testingAccom.id, name: testingAccom.name });
          }
          else {
            let tmpParameterList: any[] = [];
            let tmpAttributeList: any[] = [];

            if (testingAccom.parameters.length) {
              testingAccom.parameters.forEach((parameter: Parameter) => {
                if (parameter.name == "option") {
                  let tmpParameter: any;
                  tmpParameter = { name: "Value", value: parameter.selectedvalue };
                  tmpParameterList.push(tmpParameter);
                }
              });
            }
            let tmpAttribute: any = { name: "Candidate Selectable", parameters: tmpParameterList };
            tmpAttributeList.push(tmpAttribute);
            postTestingAccommodations.push({ id: testingAccom.id, name: testingAccom.name, "attributes": tmpAttributeList });
          }
        });
      }

      if (!!this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        tests.push({
          "exam": {
            "id": selectedAppointment.exam.id
          },
          "testingAccommodations": postTestingAccommodations,
          "sessions": []
        });
        this.selectedAppointments[0].exam.sessions.forEach((session) => {
          tests[0]["sessions"].push({
            "hold": {
              "id": session.hold.id
            }
          });
        })
      } else {
        tests.push({
          "exam": {
            "id": selectedAppointment.exam.id
          },
          "testingAccommodations": postTestingAccommodations,
          "sessions": [
            {
              "hold": {
                "id": selectedAppointment.hold.id
              }
            }
          ]
        });
      }
    });

    let bookingPostRequestData = {
      "candidate": {
        "id": ""
      },
      "tests": tests,
    };
    bookingPostRequestData['payments'] = [];
    if (this.eligibility && this.eligibility.length && this.eligibility[0] && this.eligibility[0].id) {
      // Native only expects us to send one eligibility id if there are more exams. if not the eligibilityId matching with the selected exam.
      let eligibility = this.eligibility.filter(elig => elig.exam.id === this.selectedAppointments[0].exam.id);
      bookingPostRequestData['eligibility'] =
      {
        "id": eligibility[0].id
      };
    }

    this.showPostingInProgress = true;

    // Final Schedule Call
    this.registrationApiService.postBookingDetailsWithPayment(
      cardDetail,
      bookingPostRequestData,
      this.candidate,
      this.metricsDetail,
      this.isBackToBackRequired,
      this.selectedAppointments,
      this.candidateGuid,
      voucherDetail
    )
      .subscribe(bookingPostResp => {
        if (bookingPostResp != null &&
          bookingPostResp.response &&
          bookingPostResp.response.tests
        ) {
          this.confirmationNumber = "";
          this.scheduledConfirmationNums = [];
          let postedConfirmationList: string[] = [];
          bookingPostResp.response.tests.forEach((test: any) => {

            let selectedAppointment: any = this.selectedAppointments.filter((selectedAppointment: any) => {
              return selectedAppointment.exam.id == test.exam.id;
            });

            let confnums = test.sessions.map((s: Session) => {
              if (test.sessions.length === 1) {
                selectedAppointment[0].ConfirmationNumber = s.confirmationNumber;
              } else {
                selectedAppointment[0].exam.sessions.find((se: Session) => { return se.hold.id === s.hold.id }).confirmationNumber = s.confirmationNumber;
              }
              return (s.confirmationNumber + "<br />");
            })
            this.confirmationNumber = this.confirmationNumber
              .concat(confnums + " (" + selectedAppointment[0].exam.name + ") <br />");


            this.scheduledConfirmationNums = this.scheduledConfirmationNums.concat(test.sessions.map((s: Session) => {
              return s.confirmationNumber;
            }));

          });
          this.showPostingInProgress = false;

          // set confirmation list
          this.selectedExamDetailService.setPostedConfirmationList(this.scheduledConfirmationNums);

          // Remove </br> tag end of the string
          this.confirmationNumber = this.confirmationNumber.substring(0, this.confirmationNumber.length - 6);
          //Update Document Title
          this.titleService.setTitle("ProScheduler - Appointment Scheduled");

          this.selectedAppointments.forEach((appointment) => {
            appointment.status = "scheduled";
          });

          this.selectedExamDetailService.setprevSelectedHoldObj([]);
          this.selectedExamDetailService.confirmationNumber = this.confirmationNumber;
          this.selectedExamDetailService.isPaymentConfirmed = true;

          if (this.reminderDetails.notificationSelected) {
            let emailResps: EmailResponse[] = [];
            this.reminderDetails.reminderType = "SMSText";
            if (this.scheduledConfirmationNums.length > 0) {
              this.scheduledConfirmationNums.forEach((confirmation) => {
                this.messagingApiService.text(this.reminderDetails.contactNumber, this.reminderDetails.reminderType, confirmation).subscribe(mailResponse => {
                  let emailResp: EmailResponse = new EmailResponse();
                  emailResp.successful = mailResponse.successful;
                  emailResp.emailType = "SMSText";
                  emailResps.push(emailResp);
                  if (emailResps.length == this.scheduledConfirmationNums.length) {
                    this.emailResponse = emailResp;
                    this.messagingApiService.setEmailState(emailResp);
                  }
                }, (err) => {
                  let emailResponse: EmailResponse = new EmailResponse();
                });
              });
            }
          }
          this.routingService.scheduleFwdNavigation(ComponentType.PaymentComponent);
        }
        else {
          this.errorMessage = {
            id: SchedulingErrors.Default,
            message: 'errors.error',
            description: 'errors.thereWasAnIssue'
          };
          this.showPostingInProgress = false;
        }
      }, (err) => {
        this.errorMessage = {
          id: SchedulingErrors.Default,
          message: 'errors.error',
          description: 'errors.thereWasAnIssue'
        };
        this.showPostingInProgress = false;
      });
  }

  redirectToPayment(): void {
    this.timerService.clearClock();
    this.loaderService.showActive();

    let paymentDetails: CreatePaymentSessionRequest = new CreatePaymentSessionRequest(
      Math.abs(this.totalAmount - this.discountAmount),
      this.currencyCode,
      this.client.code,
      this.program.code,
      this.selectedAppointments[this.selectedAppointments.length - 1].exam.code,
      CardFee.Schedule,
      this.selectedExamDetailService.isProctored,
      !!this.selectedExamDetailService.terminationUrl ? this.selectedExamDetailService.terminationUrl : undefined
    );
    let examDetails: any[] = [];
    let voucherExamDetails: any[] = [];
    let voucherDetail: any = null;

    this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
      if (!this.isVoucherFullPayment) {
        examDetails.push({
          "examId": appointment.exam.id,
          "amount": this.isVoucherFullPayment ? 0 : appointment.payments[0].amount - this.discountAmount
        });
      }
      if (this.selectedExamDetailService.voucherTransactionId) {
        voucherExamDetails.push({
          "examId": appointment.exam.id,
          "amount": this.isVoucherFullPayment ? appointment.payments[0].amount : this.discountAmount
        });
      }
    });

    if (this.selectedExamDetailService.voucherTransactionId) {
      voucherDetail = {
        "card": this.voucherDetail,
        "sessions": voucherExamDetails,
        "candidateId": '',
        "voucherTransactionId": this.selectedExamDetailService.voucherTransactionId,
      };
    }

    let tests: any[] = [];
    this.selectedAppointments.forEach((selectedAppointment: AppointmentDetail) => {
      let postTestingAccommodations: any[] = [];
      if (selectedAppointment.exam.testingAccommodations != undefined && selectedAppointment.exam.testingAccommodations.length) {
        selectedAppointment.exam.testingAccommodations.forEach((testingAccom: TestingAccommodation) => {
          if (testingAccom.controlType != "LIST") {
            postTestingAccommodations.push({ id: testingAccom.id, name: testingAccom.name });
          }
          else {
            let tmpParameterList: any[] = [];
            let tmpAttributeList: any[] = [];

            if (testingAccom.parameters.length) {
              testingAccom.parameters.forEach((parameter: Parameter) => {
                if (parameter.name == "option") {
                  let tmpParameter: any;
                  tmpParameter = { name: "Value", value: parameter.selectedvalue };
                  tmpParameterList.push(tmpParameter);
                }
              });
            }
            let tmpAttribute: any = { name: "Candidate Selectable", parameters: tmpParameterList };
            tmpAttributeList.push(tmpAttribute);
            postTestingAccommodations.push({ id: testingAccom.id, name: testingAccom.name, "attributes": tmpAttributeList });
          }
        });
      }

      if (!!this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        tests.push({
          "exam": {
            "id": selectedAppointment.exam.id
          },
          "testingAccommodations": postTestingAccommodations,
          "sessions": []
        });
        this.selectedAppointments[0].exam.sessions.forEach((session) => {
          tests[0]["sessions"].push({
            "hold": {
              "id": session.hold.id
            }
          });
        })
      } else {
        tests.push({
          "exam": {
            "id": selectedAppointment.exam.id
          },
          "testingAccommodations": postTestingAccommodations,
          "sessions": [
            {
              "hold": {
                "id": selectedAppointment.hold.id
              }
            }
          ]
        });
      }
    });

    let bookingPostRequestData = {
      "candidate": {
        "id": ""
      },
      "tests": tests,
    };
    bookingPostRequestData['payments'] = [];
    if (this.eligibility && this.eligibility.length && this.eligibility[0] && this.eligibility[0].id) {
      // Native only expects us to send one eligibility id if there are more exams. if not the eligibilityId matching with the selected exam.
      let eligibility = this.eligibility.filter(elig => elig.exam.id === this.selectedAppointments[0].exam.id);
      bookingPostRequestData['eligibility'] =
      {
        "id": eligibility[0].id
      };
    }

    this.showPostingInProgress = true;

    // Final prep before redirect
    this.registrationApiService.prepareBookingPost(
      paymentDetails,
      bookingPostRequestData,
      this.candidate,
      this.candidateGuid
    )
      .subscribe((prepResponse) => {
        if ((!!prepResponse && prepResponse.status) && (!!this.paymentOrchestratorSessionDetail && !!this.paymentOrchestratorSessionDetail.paymentOrchestratorUrl)) {
          this.selectedExamDetailService.setVoucherRequestData(voucherDetail);
          this.selectedExamDetailService.setExamDetailsForSettlement(examDetails);
          this.selectedExamDetailService.ssoToken = this.bootstrapParams.Token;
          this.metricApiService.log(LogLevel.Info, 'Attempting to set session data in local storage.');
          let storageErr = this.selectedExamDetailService.saveToLocalStorage(this.bootstrapParams.sessionGuid);

          this.showPostingInProgress = false;
          this.isBookingInProgress = false;

          if (!storageErr) {
            this.metricApiService.log(LogLevel.Info, `Successfully set local storage session data.`);
            this.routingService.navigateToUrl(this.paymentOrchestratorSessionDetail.paymentOrchestratorUrl);
          } else if (!!storageErr && !!storageErr.message.id) {
            this.metricApiService.log(LogLevel.Debug, `Error setting session data in local storage: ${JSON.stringify(storageErr.error)}`);
            this.errorMessage = storageErr.message;
            this.showPostingInProgress = false;
            this.isBookingInProgress = false;
            this.loaderService.hideActive();

            bookingPostRequestData['tests'][0]['sessions'].forEach((session: any) => {
              this.schedulingApiService.releaseHold(session.hold).subscribe(() => { });
            });
          }
        } else if (!!prepResponse.errorId) {
          this.errorMessage = {
            id: prepResponse.errorId,
            message: 'errors.error',
            description: prepResponse.message
          };
          this.showPostingInProgress = false;
          this.isBookingInProgress = false;
          this.loaderService.hideActive();
        }
      }, (err) => {
        this.errorMessage = {
          id: SchedulingErrors.Default,
          message: 'errors.error',
          description: 'errors.thereWasAnIssue'
        };
        this.showPostingInProgress = false;
        this.isBookingInProgress = false;
        this.loaderService.hideActive();
      });
  }

  /* resets voucher on voucher number change */
  resetVoucher() {
    this.selectedExamDetailService.setDiscountAmount(0);
    this.balance = 0;
    this.selectedExamDetailService.isVoucherFullPayment = false;
    this.isVoucherFullPayment = false;
    this.selectedExamDetailService.voucherTransactionId = '';
    this.isVoucherValid = false;
    this.selectedExamDetailService.setVoucherDetail(new CardDetail());
  }

  /* Navigate away from proscheduler(to term/prometric site) */
  goToPrometric(): void {
    this.routingService.navigateAwayFromProscheduler();
  }
}
